import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import logoMain from "@/assets/images/nscs_logo_color.svg";
import logoMainWhite from "@/assets/images/nscs_logo_white.svg";
import { NavigationItem } from "@/navigation/types";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import navConfig from "@/navigation/navigation";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
} from "@/components/ui/sidebar";
import { RootState } from "@/app/rootReducer";
import { useSelector } from "react-redux";

/**
 * The full WideSidebar component with type-safe role filtering
 * for navigation items and their children.
 */
export default function WideSidebar() {
  const { theme } = useTheme();
  const navItems = navConfig.navigation;

  // 1) Grab the logged-in user’s roles from userAppSettings in Redux
  const userRoles = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings?.roles || [],
  );

  // Manage sub-menu open/closed state
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  /**
   * Check if user has at least one role required by the item (if any).
   */
  function userHasRequiredRoles(item: NavigationItem): boolean {
    if (!item.roles || item.roles.length === 0) {
      // No specific roles required
      return true;
    }
    // Return true if user has at least one of the required roles
    return item.roles.some((role) => userRoles.includes(role));
  }

  /**
   * Filter nav items recursively based on user roles.
   */
  function filterNavItemsByRoles(
    items: NavigationItem[],
    roles: string[],
  ): NavigationItem[] {
    const filteredItems: NavigationItem[] = [];

    for (const item of items) {
      // If user doesn't have the required roles, skip this item
      if (!userHasRequiredRoles(item)) {
        continue;
      }

      let filteredChildren: NavigationItem[] | undefined = undefined;
      if (item.children && item.children.length > 0) {
        filteredChildren = filterNavItemsByRoles(item.children, roles);
      }

      filteredItems.push({
        ...item,
        children: filteredChildren,
      });
    }

    return filteredItems;
  }

  // Get a filtered copy of navItems
  const filteredNavItems = filterNavItemsByRoles(navItems, userRoles);

  function handleToggleItem(itemKey: string) {
    setOpenItems((prev) => ({ ...prev, [itemKey]: !prev[itemKey] }));
  }

  /**
   * Recursively render the navigation items (and sub-items).
   */
  function renderNavigation(items: NavigationItem[]) {
    return items.map((item) => {
      const itemKey = item.internalName || item.name;
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = !!openItems[itemKey];

      if (hasChildren) {
        return (
          <SidebarMenuItem
            data-state={isOpen ? "open" : undefined}
            key={itemKey}
          >
            <SidebarMenuButton
              data-state={isOpen ? "open" : undefined}
              onClick={() => handleToggleItem(itemKey)}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span>{item.name}</span>
              <ChevronRightIcon
                aria-hidden="true"
                className={`ml-auto h-4 w-4 transition-transform ${
                  isOpen ? "rotate-90" : ""
                }`}
              />
            </SidebarMenuButton>
            {isOpen && (
              <SidebarMenuSub>
                {renderNavigation(item.children!)}
              </SidebarMenuSub>
            )}
          </SidebarMenuItem>
        );
      }

      // No children: render a leaf item with a link
      return (
        <SidebarMenuItem key={itemKey}>
          <SidebarMenuButton asChild>
            <Link title={item.name} to={item.href ?? "#"}>
              <item.icon className="h-4 w-4 shrink-0" />
              <span>{item.name}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });
  }

  return (
    <div className="flex-shrink-0 h-screen">
      <Sidebar
        className="h-full"
        collapsible="offcanvas"
        side="left"
        variant="sidebar"
      >
        <SidebarHeader className="border-b border-sidebar-border">
          <div className="flex flex-col space-y-3 px-2 py-2">
            <img
              alt="Your Company"
              className="h-8 w-auto"
              src={theme === "dark" ? logoMainWhite : logoMain}
            />
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>{renderNavigation(filteredNavItems)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    </div>
  );
}
